import { useCallback, useState } from 'react'
import { downloadBlob } from '@sequencehq/utils'
import { DownloadZipConfig, ZipDownloadStatus } from './types'

/**
 * Fetches the zip artifact produced by a terminal download bulk action and
 * saves it via `downloadBlob`, exposing its progress as `status` so the caller
 * can fold it into existing UI (the in-flight panel) rather than showing a
 * separate toast. How to fetch and name the zip comes from the entity's
 * `downloadZip` config, so this hook stays entity-agnostic; without a config
 * (a non-download entity) `download` is a no-op and callers can call the hook
 * unconditionally.
 */
export const useDownloadBulkActionZip = (
  bulkActionId: string,
  downloadZip: DownloadZipConfig | undefined
) => {
  const [status, setStatus] = useState<ZipDownloadStatus>('idle')

  const download = useCallback(async () => {
    if (!downloadZip) {
      return
    }

    setStatus('preparing')

    try {
      const res = await downloadZip.fetch({ bulkActionId })

      if (!res.data) {
        throw new Error(downloadZip.errorMessage)
      }

      downloadBlob(downloadZip.filename(bulkActionId))(res.data)
      setStatus('ready')
    } catch {
      setStatus('error')
    }
  }, [bulkActionId, downloadZip])

  return { download, status }
}
